import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { Button } from 'primeng/button';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Beneficjent } from '../../models/beneficjent';
import { Card } from 'primeng/card';
import { PreviewAccountNumberComponent } from '../bank-account-number/preview-account-number/preview-account-number.component';
import { PreviewBeneficiaryAddressComponent } from '../../../pages/beneficiary-settings/components/preview-beneficiary-address/preview-beneficiary-address.component';
import { PreviewBeneficiaryDetailsComponent } from '../beneficiary/preview-beneficiary-details/preview-beneficiary-details.component';
import { PreviewContactDetailsComponent } from '../beneficiary/preview-contact-details/preview-contact-details.component';
import { InfoMessageComponent } from '../info-message/info-message.component';
import { catchError, EMPTY, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpResponseService } from '../../../core/services/http-error-response.service';
import { UserApi } from '../../services/api/user.api';
import { EditBeneficiaryFormBuilder } from '../../../pages/edit-beneficiary/form-group/edit-beneficiary.form.builder';
import { AuthService } from '../../../core/services/auth.service';
import { FormsUtils } from '../../utils/forms.utils';
import { Message } from 'primeng/message';
import { AddressFormBuilder } from '../address/form-address/form-group/address.form.builder';

@Component({
  selector: 'verify-user-data-dialog',
  imports: [
    Button,
    Card,
    PreviewAccountNumberComponent,
    PreviewBeneficiaryAddressComponent,
    PreviewBeneficiaryDetailsComponent,
    PreviewContactDetailsComponent,
    InfoMessageComponent,
    Message,
  ],
  providers: [UserApi],
  templateUrl: './verify-user-data-dialog.html',
  styleUrl: './verify-user-data-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class VerifyUserDataDialog implements OnInit {
  protected readonly dynamicDialogRef = inject(DynamicDialogRef);
  readonly #authService = inject(AuthService);
  protected beneficiary = input.required<Beneficjent>();
  protected isUserDataValid = signal<boolean>(false);

  readonly #destroyRef = inject(DestroyRef);
  readonly #userApi = inject(UserApi);
  readonly #httpResponseService = inject(HttpResponseService);

  ngOnInit(): void {
    this.#validateData();
  }

  protected verifyData(): void {
    if (this.isUserDataValid()) {
      this.#userApi
        .verifyData$()
        .pipe(
          tap(() => {
            this.dynamicDialogRef.close('confirm');
          }),
          catchError((err) => {
            this.#httpResponseService.showErrorMessage(
              err,
              'Wystąpił błąd podczas potwierdzania danych',
            );
            this.dynamicDialogRef.close('edit-data');
            return EMPTY;
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  #validateData(): void {
    const fg = EditBeneficiaryFormBuilder.createFormGroup(
      this.#authService.getUser()!,
    );
    EditBeneficiaryFormBuilder.setAvailabilityOfBankAccount(fg, true);
    AddressFormBuilder.switchDomesticOrForeignGroup(
      fg.controls.address.controls.adres,
      true,
    );
    AddressFormBuilder.switchDomesticOrForeignGroup(
      fg.controls.address.controls.adresKorespondencyjny,
      true,
    );
    this.isUserDataValid.set(FormsUtils.isReadyForSave(fg));
  }
}
